import {
  ContextualMenu,
  getTheme,
  IconButton,
  IContextualMenuItem,
  IContextualMenuProps,
  SharedColors
} from "@fluentui/react";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { logEvent, logException } from "../helper/AppInsightsHelper";
import { IUser, selectAuthUser } from "../slices/Auth.slice";
import { useAppSelector } from "../store/hooks";
import { SHARE_LINK_CONSTANTS as constants } from "./Constants/ShareLinkContextMenu.constants";
import { ShareLinkContextMenuProps } from "./Interfaces/ShareLinkContextMenu.interfaces";

export const ShareLinkContextMenu: React.FC<ShareLinkContextMenuProps> = ({
  pageType,
  onDismiss,
  customUrl,
  entityName,
  onShareError,
  onShareSuccess,
  additionalMenuProps
}) => {
  const theme = getTheme();
  const {
    roles,
    sid: sessionId,
    iss: tokenIssuer,
    name: accountName,
    aud: tokenAudience,
    iat: tokenIssuedAt,
    nbf: tokenNotBefore,
    exp: tokenExpiration,
    oid: accountObjectId,
    email
  } = useAppSelector((state) => selectAuthUser(state) || ({} as IUser));
  const shareButtonRef = useRef<HTMLDivElement>(null);
  const [hidden, setHidden] = useState(true);

  // Memoize the share button click handler
  const handleShareButtonClick = useCallback(() => {
    setHidden((prev) => !prev);
    logEvent("Share Link Button Clicked", "Share Link", "ShareLinkContextMenu", { pageType, entityName });
  }, []);

  // Memoize the share menu dismiss handler
  const handleShareMenuDismiss = useCallback(
    (event?: Event | React.MouseEvent | React.KeyboardEvent, dismissAll?: boolean) => {
      setHidden(true);
      onDismiss?.(event, dismissAll);
    },
    []
  );

  // Get the URL to share
  const getUrlToShare = useCallback(() => {
    return customUrl || globalThis.location.href;
  }, [customUrl]);

  // Handle copy link to clipboard
  const handleCopyLink = useCallback(
    async (event?: React.MouseEvent<HTMLElement, MouseEvent> | React.KeyboardEvent<HTMLElement> | undefined) => {
      const urlToCopy = getUrlToShare();
      try {
        await navigator.clipboard.writeText(urlToCopy);

        logEvent(
          constants.EVENT_CATEGORIES.COPY_LINK(pageType),
          constants.EVENT_CATEGORIES.PAGE(pageType),
          constants.EVENTS.COPY_LINK,
          {
            entityName,
            urlToCopy,
            userClaims: {
              tokenAudience,
              tokenIssuer,
              tokenIssuedAt,
              tokenNotBefore,
              tokenExpiration,
              name: accountName,
              accountObjectId,
              email,
              roles,
              sessionId
            }
          }
        );
        onShareSuccess?.(constants.EVENTS.COPY_LINK);
      } catch (error) {
        logException(
          constants.EVENT_CATEGORIES.PAGE(pageType),
          constants.ERROR_CATEGORIES.COPY_URL,
          constants.EVENTS.COPY_LINK,
          error,
          {
            entityName,
            urlToCopy,
            userClaims: {
              tokenAudience,
              tokenIssuer,
              tokenIssuedAt,
              tokenNotBefore,
              tokenExpiration,
              name: accountName,
              accountObjectId,
              email,
              roles,
              sessionId
            }
          }
        );
        onShareError?.(constants.EVENTS.COPY_LINK, error);
      }

      onDismiss?.(event, true);
    },
    [pageType, entityName, getUrlToShare, onShareSuccess, onShareError, onDismiss]
  );

  // Handle sharing to Outlook Web
  const handleShareToOutlookWeb = useCallback(
    (event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
      const urlToShare = getUrlToShare();
      try {
        const subject = encodeURIComponent(constants.EMAIL_SUBJECT_TEMPLATE(entityName));
        const body = encodeURIComponent(constants.EMAIL_BODY_TEMPLATE(entityName, urlToShare));

        const outlookWebUrl = `${constants.OUTLOOK_WEB_URL}?subject=${subject}&body=${body}`;
        window.open(outlookWebUrl, constants.WINDOW_OPTIONS.TARGET_BLANK, constants.WINDOW_OPTIONS.SECURE);

        logEvent(
          constants.EVENT_CATEGORIES.SHARE_OUTLOOK_WEB(pageType),
          constants.EVENT_CATEGORIES.PAGE(pageType),
          constants.EVENTS.SHARE_OUTLOOK_WEB,
          {
            entityName,
            urlToShare,
            userClaims: {
              tokenAudience,
              tokenIssuer,
              tokenIssuedAt,
              tokenNotBefore,
              tokenExpiration,
              name: accountName,
              accountObjectId,
              email,
              roles,
              sessionId
            }
          }
        );
        onShareSuccess?.(constants.EVENTS.SHARE_OUTLOOK_WEB);
      } catch (error) {
        logException(
          constants.EVENT_CATEGORIES.PAGE(pageType),
          constants.ERROR_CATEGORIES.SHARE_OUTLOOK_WEB,
          constants.EVENTS.SHARE_OUTLOOK_WEB,
          error,
          {
            entityName,
            urlToShare,
            userClaims: {
              tokenAudience,
              tokenIssuer,
              tokenIssuedAt,
              tokenNotBefore,
              tokenExpiration,
              name: accountName,
              accountObjectId,
              email,
              roles,
              sessionId
            }
          }
        );
        onShareError?.(constants.EVENTS.SHARE_OUTLOOK_WEB, error);
      }

      onDismiss?.(event, true);
    },
    [pageType, entityName, getUrlToShare, onShareSuccess, onShareError, onDismiss]
  );

  // Handle sharing to Outlook (mailto)
  const handleShareToOutlook = useCallback(
    (event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
      const urlToShare = getUrlToShare();
      try {
        const subject = encodeURIComponent(constants.EMAIL_SUBJECT_TEMPLATE(entityName));
        const body = encodeURIComponent(constants.EMAIL_BODY_TEMPLATE(entityName, urlToShare));

        const outlookUrl = `mailto:?subject=${subject}&body=${body}`;
        window.open(outlookUrl, constants.WINDOW_OPTIONS.TARGET_BLANK);

        logEvent(
          constants.EVENT_CATEGORIES.SHARE_OUTLOOK(pageType),
          constants.EVENT_CATEGORIES.PAGE(pageType),
          constants.EVENTS.SHARE_OUTLOOK,
          {
            entityName,
            urlToShare,
            userClaims: {
              tokenAudience,
              tokenIssuer,
              tokenIssuedAt,
              tokenNotBefore,
              tokenExpiration,
              name: accountName,
              accountObjectId,
              email,
              roles,
              sessionId
            }
          }
        );
        onShareSuccess?.(constants.EVENTS.SHARE_OUTLOOK);
      } catch (error) {
        logException(
          constants.EVENT_CATEGORIES.PAGE(pageType),
          constants.ERROR_CATEGORIES.SHARE_OUTLOOK,
          constants.EVENTS.SHARE_OUTLOOK,
          error,
          {
            entityName,
            urlToShare,
            userClaims: {
              tokenAudience,
              tokenIssuer,
              tokenIssuedAt,
              tokenNotBefore,
              tokenExpiration,
              name: accountName,
              accountObjectId,
              email,
              roles,
              sessionId
            }
          }
        );
        onShareError?.(constants.EVENTS.SHARE_OUTLOOK, error);
      }

      onDismiss?.(event, true);
    },
    [pageType, entityName, getUrlToShare, onShareSuccess, onShareError, onDismiss]
  );

  // Handle sharing to Microsoft Teams
  const handleShareToTeams = useCallback(
    (event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
      const urlToShare = getUrlToShare();
      try {
        const text = encodeURIComponent(constants.TEAMS_MESSAGE_TEMPLATE(entityName, urlToShare));

        const teamsUrl = `${constants.TEAMS_SHARE_URL}?href=${encodeURIComponent(urlToShare)}&msgText=${text}`;
        window.open(teamsUrl, constants.WINDOW_OPTIONS.TARGET_BLANK, constants.WINDOW_OPTIONS.SECURE);

        logEvent(
          constants.EVENT_CATEGORIES.SHARE_TEAMS(pageType),
          constants.EVENT_CATEGORIES.PAGE(pageType),
          constants.EVENTS.SHARE_TEAMS,
          {
            entityName,
            urlToShare,
            userClaims: {
              tokenAudience,
              tokenIssuer,
              tokenIssuedAt,
              tokenNotBefore,
              tokenExpiration,
              name: accountName,
              accountObjectId,
              email,
              roles,
              sessionId
            }
          }
        );
        onShareSuccess?.(constants.EVENTS.SHARE_TEAMS);
      } catch (error) {
        logException(
          constants.EVENT_CATEGORIES.PAGE(pageType),
          constants.ERROR_CATEGORIES.SHARE_TEAMS,
          constants.EVENTS.SHARE_TEAMS,
          error,
          {
            entityName,
            urlToShare,
            userClaims: {
              tokenAudience,
              tokenIssuer,
              tokenIssuedAt,
              tokenNotBefore,
              tokenExpiration,
              name: accountName,
              accountObjectId,
              email,
              roles,
              sessionId
            }
          }
        );
        onShareError?.(constants.EVENTS.SHARE_TEAMS, error);
      }

      onDismiss?.(event, true);
    },
    [pageType, entityName, getUrlToShare, onShareSuccess, onShareError, onDismiss]
  );

  // Menu items
  const menuItems: IContextualMenuItem[] = useMemo(
    () => [
      {
        key: constants.MENU_ITEMS.COPY_LINK,
        text: constants.MENU_TEXTS.COPY_LINK,
        iconProps: { iconName: constants.ICONS.COPY },
        onClick: (event) => {
          handleCopyLink(event);
        },
        title: constants.MENU_TITLES.COPY_LINK
      },
      {
        key: constants.MENU_ITEMS.DIVIDER,
        itemType: constants.ITEM_TYPES.DIVIDER,
        text: constants.MENU_TEXTS.DIVIDER
      },
      {
        key: constants.MENU_ITEMS.TEAMS,
        text: constants.MENU_TEXTS.TEAMS,
        iconProps: { iconName: constants.ICONS.TEAMS_LOGO },
        onClick: handleShareToTeams,
        title: constants.MENU_TITLES.TEAMS
      },
      {
        key: constants.MENU_ITEMS.OUTLOOK_WEB,
        text: constants.MENU_TEXTS.OUTLOOK_WEB,
        iconProps: { iconName: constants.ICONS.OUTLOOK_LOGO },
        onClick: handleShareToOutlookWeb,
        title: constants.MENU_TITLES.OUTLOOK_WEB
      },
      {
        key: constants.MENU_ITEMS.OUTLOOK_DESKTOP,
        text: constants.MENU_TEXTS.OUTLOOK_DESKTOP,
        iconProps: { iconName: constants.ICONS.MAIL },
        onClick: handleShareToOutlook,
        title: constants.MENU_TITLES.OUTLOOK_DESKTOP
      }
    ],
    [handleCopyLink, handleShareToOutlookWeb, handleShareToOutlook, handleShareToTeams]
  );

  const contextualMenuPropsWithDefaults: IContextualMenuProps = {
    hidden: hidden,
    items: menuItems,
    isBeakVisible: true,
    shouldFocusOnContainer: true,
    onDismiss: handleShareMenuDismiss,
    styles: {
      root: {
        minWidth: constants.STYLES.ROOT_MIN_WIDTH
      },
      subComponentStyles: {
        menuItem: {
          root: {
            fontSize: constants.STYLES.FONT_SIZE
          },
          icon: {
            color: theme.palette.themePrimary
          }
        }
      }
    },
    ...additionalMenuProps
  };

  return (
    <div>
      <div ref={shareButtonRef}>
        <IconButton
          aria-label={constants.ICONS.SHARE}
          iconProps={{ iconName: constants.ICONS.SHARE }}
          onClick={handleShareButtonClick}
          title={constants.MENU_TITLES.SHARE_BUTTON}
          styles={{
            root: {
              border: `1px solid ${SharedColors.cyanBlue10}`,
              borderRadius: constants.STYLES.BORDER_RADIUS
            }
          }}
        />
      </div>
      <ContextualMenu {...contextualMenuPropsWithDefaults} target={shareButtonRef.current} />
    </div>
  );
};

export default ShareLinkContextMenu;
